import { useToggle } from 'usehooks-ts';
import React, { useContext, useRef } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { Button } from '@progress/kendo-react-buttons';
import { ChatMessageTemplateProps } from '@progress/kendo-react-conversational-ui';

import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import { GameContext } from '../../contexts/game';
import { PlayerContext } from '../../contexts/player';
import { PostTradeRequestOrConfirmAsync } from '../../services/trade';
import { GameDocumentContext } from '../../contexts/game-document';

interface MessageTemplateProps extends ChatMessageTemplateProps {
  currentUserCode: string;
  hideChatButton?: boolean;
  onClickDirectChatButton?: (playerCode: string) => void;
}

export const MessageTemplate = React.memo((props: MessageTemplateProps) => {
  const [showMenu, setShowMenu] = useToggle(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [player] = useContext(PlayerContext);
  const [state] = useContext(GameDocumentContext);
  const [game] = useContext(GameContext);
  const [notification, setNotification] = useContext(NotificationContext);

  const isInLobby = ['waiting', 'ready'].includes(
    player.playerState?.status ?? ''
  );
  const preGameSetting = state.gameDocument?.settings.preGame;
  const inGameSetting =
    game.gameState?.runtime?.inGame ?? state.gameDocument?.settings.inGame;
  const showTrade = isInLobby
    ? preGameSetting?.showPreGameTrade
    : inGameSetting?.showInGameTrade;

  const onClickDirectChatButton = (playerCode: string) => {
    if (props.onClickDirectChatButton) {
      props.onClickDirectChatButton(playerCode);
    }
  };

  const onClickTradeButton = async (requestTo: string) => {
    if (game.gameCode) {
      let notificationContent: NotificationContent | null = null;
      try {
        const resp = await PostTradeRequestOrConfirmAsync(
          game.gameCode,
          'requests',
          {
            offerPlayerCode: props.currentUserCode,
            requestPlayerCode: requestTo
          }
        );

        if (resp.requestPlayerStatus === 'Draft') {
          notificationContent = {
            icon: 'notifications',
            isHide: false,
            message: `Trade request sent.`
          };
        }
      } catch (error: any) {
        if (error.data) {
          notificationContent = {
            icon: 'notifications',
            isHide: false,
            message: `${error.data}`
          };
        }
      }

      if (notificationContent !== null) {
        const content: NotificationContent[] = notification.content;
        content.push(notificationContent);
        setNotification({ ...notification, content });
        setShowMenu();
      }
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMenu &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setShowMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="d-flex">
      <div className="k-chat-bubble">
        <div>{props.item.text}</div>
      </div>
      {props.currentUserCode !== props.item.author.id && showTrade && (
        <>
          <button
            style={{
              background: 'none',
              border: 'none'
            }}
            onClick={setShowMenu}
            ref={anchorRef}>
            <span className="material-symbols-outlined">more_horiz</span>
          </button>
          <Popup
            anchor={anchorRef.current}
            show={showMenu}
            popupClass="custom-popup chat-popup">
            <div className="dropdown-content d-flex flex-column" ref={menuRef}>
              <Button onClick={() => onClickTradeButton(props.item.author.id)}>
                <div className="d-flex">
                  <span className="material-symbols-outlined mr-2">
                    partner_exchange
                  </span>
                  Trade
                </div>
              </Button>
              {!props.hideChatButton && (
                <Button
                  onClick={() => {
                    onClickDirectChatButton(props.item.author.id);
                  }}>
                  <div className="d-flex">
                    <span className="material-symbols-outlined mr-2">
                      chat_bubble
                    </span>
                    Chat
                  </div>
                </Button>
              )}
            </div>
          </Popup>
        </>
      )}
    </div>
  );
});
